import { useState, useEffect, useRef } from "react";

export type TradeRole = "buyer" | "supplier" | "both";

export interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  country: string;
  mobile: string;
  password: string;
  confirmPassword: string;
  agreeToTerms: boolean;
}

export interface SyncRequest {
  email?: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  phone?: string;
  role?: string;
  // Business registration fields
  country?: string;
  businessType?: string;
  password?: string;
}

export type ShopNameStatus = "idle" | "checking" | "available" | "taken";

export interface UseRegistrationFormProps {
  onSyncUser?: (syncData: SyncRequest) => Promise<any>;
  onGetUserStatus?: () => Promise<{ status: string; user?: any }>;
  onRegister?: (
    data: SyncRequest & { password?: string; captchaToken?: string },
  ) => Promise<any>;
  onRegistrationSuccess?: (userRole: string) => void;
  onCheckShopNameAvailable?: (name: string) => Promise<{ available: boolean }>;
  auth0Email?: string;
  isDirectRegistration?: boolean;
  getCaptchaToken?: () => string | null | Promise<string | null>;
  resetCaptcha?: () => void;
  /** When set (e.g. from multi-step flow Step 1), role is fixed and not user-editable. */
  initialRole?: TradeRole;
  /** Prefill email when coming from multi-step Step 0 (email or OAuth). */
  prefilledEmail?: string;
}

export const useRegistrationForm = (props?: UseRegistrationFormProps) => {
  const initialRole = props?.initialRole ?? "buyer";
  const [tradeRole, setTradeRole] = useState<TradeRole>(initialRole);
  const prefilled = props?.prefilledEmail ?? props?.auth0Email ?? "";
  const [formData, setFormData] = useState<FormData>({
    firstName: "",
    lastName: "",
    email: prefilled,
    company: "",
    country: "",
    mobile: "",
    password: "",
    confirmPassword: "",
    agreeToTerms: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [shopNameStatus, setShopNameStatus] = useState<ShopNameStatus>("idle");
  const shopNameCheckRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const companyValueRef = useRef(formData.company);
  companyValueRef.current = formData.company;

  const clearSubmitError = () => setSubmitError(null);

  const isSellerRole = tradeRole === "supplier" || tradeRole === "both";
  const isFormComplete =
    formData.firstName.trim() !== "" &&
    formData.lastName.trim() !== "" &&
    formData.email.trim() !== "" &&
    (isSellerRole ? formData.company.trim() !== "" : true) &&
    formData.country !== "" &&
    formData.mobile.trim() !== "" &&
    formData.password.length >= 8 &&
    formData.password === formData.confirmPassword &&
    formData.agreeToTerms === true;

  // Sync tradeRole when initialRole changes (e.g. multi-step flow)
  useEffect(() => {
    if (props?.initialRole != null) {
      setTradeRole(props.initialRole);
    }
  }, [props?.initialRole]);

  // Prefill email from prop (e.g. social login, multi-step) or from getMe on mount (skip when direct registration)
  useEffect(() => {
    if (props?.prefilledEmail) {
      setFormData((prev) => ({ ...prev, email: props.prefilledEmail! }));
      return;
    }
    if (props?.auth0Email) {
      setFormData((prev) => ({ ...prev, email: props.auth0Email! }));
      return;
    }
    if (props?.isDirectRegistration) return;
    if (props?.onGetUserStatus) {
      props
        .onGetUserStatus()
        .then((res) => {
          if (res?.user?.email) {
            setFormData((prev) => ({ ...prev, email: res.user.email }));
          }
        })
        .catch(() => {});
    }
  }, [props?.prefilledEmail, props?.auth0Email, props?.isDirectRegistration]);

  // Debounced shop name availability check
  useEffect(() => {
    const trimmed = formData.company.trim();
    if (!trimmed) {
      setShopNameStatus("idle");
      return;
    }
    const checkFn = props?.onCheckShopNameAvailable;
    if (!checkFn) {
      setShopNameStatus("idle");
      return;
    }
    if (shopNameCheckRef.current) clearTimeout(shopNameCheckRef.current);
    shopNameCheckRef.current = setTimeout(() => {
      shopNameCheckRef.current = null;
      const valueToCheck = trimmed;
      setShopNameStatus("checking");
      checkFn(valueToCheck)
        .then((res) => {
          if (companyValueRef.current.trim() === valueToCheck) {
            setShopNameStatus(res.available ? "available" : "taken");
          }
        })
        .catch(() => {
          if (companyValueRef.current.trim() === valueToCheck)
            setShopNameStatus("idle");
        });
    }, 400);
    return () => {
      if (shopNameCheckRef.current) clearTimeout(shopNameCheckRef.current);
    };
  }, [formData.company, props?.onCheckShopNameAvailable]);

  const handleInputChange = (
    field: keyof FormData,
    value: string | boolean,
  ) => {
    setSubmitError(null);
    let next: string | boolean = value;
    if (field === "mobile" && typeof value === "string") {
      next = value.replace(/[^0-9]/g, "");
    }
    setFormData((prev) => ({
      ...prev,
      [field]: next,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setIsLoading(true);

    try {
      // Validate password and confirmPassword match
      if (formData.password !== formData.confirmPassword) {
        setSubmitError("Passwords do not match. Please try again.");
        setIsLoading(false);
        return;
      }

      // Validate password strength (minimum 8 characters)
      if (formData.password.length < 8) {
        setSubmitError("Password must be at least 8 characters long.");
        setIsLoading(false);
        return;
      }

      const role =
        tradeRole === "supplier"
          ? "seller"
          : tradeRole === "both"
            ? "both"
            : "buyer";
      const companyName =
        tradeRole === "buyer"
          ? formData.company.trim() ||
            (formData.firstName.trim()
              ? `${formData.firstName.trim()}'s Company`
              : "My Company")
          : formData.company.trim();
      const syncData: SyncRequest = {
        email: formData.email,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        companyName,
        phone: formData.mobile,
        role,
        country: formData.country,
        businessType: tradeRole,
        password: formData.password,
      };

      // Direct registration (no token): use register API
      if (props?.isDirectRegistration && props?.onRegister) {
        const rawToken = props?.getCaptchaToken?.();
        const captchaToken = rawToken != null ? await Promise.resolve(rawToken) : undefined;
        if (
          props?.getCaptchaToken != null &&
          (!captchaToken || !String(captchaToken).trim())
        ) {
          setSubmitError("CAPTCHA verification failed. Please try again.");
          setIsLoading(false);
          return;
        }
        await props.onRegister({
          ...syncData,
          password: formData.password,
          captchaToken: captchaToken ?? undefined,
        });
        if (props?.onRegistrationSuccess) {
          props.onRegistrationSuccess(syncData.role || "buyer");
        }
        return;
      }

      // Post-OAuth or logged-in: get status then sync
      let userStatus = "not_registered";
      if (props?.onGetUserStatus) {
        try {
          const statusResponse = await props.onGetUserStatus();
          userStatus = statusResponse.status;
        } catch {
          userStatus = "not_registered";
        }
      }

      if (props?.onSyncUser) {
        if (userStatus === "not_registered") {
          await props.onSyncUser(syncData);
        } else if (userStatus === "pending_registration") {
          await props.onSyncUser(syncData);
        } else if (userStatus === "active") {
          await props.onSyncUser(syncData);
        } else {
          await props.onSyncUser(syncData);
        }
      }

      // Handle success - call success callback if provided
      if (props?.onRegistrationSuccess) {
        const userRole = syncData.role || "buyer";
        console.log(
          "Registration successful, calling success callback with role:",
          userRole,
        );
        props.onRegistrationSuccess(userRole);
      }
    } catch (error: unknown) {
      console.error("Error syncing user data:", error);
      props?.resetCaptcha?.();
      const err = error as {
        message?: string;
        response?: { data?: { message?: string }; status?: number };
      };
      const message =
        err?.message ||
        err?.response?.data?.message ||
        (err?.response?.status === 409
          ? "An account with this email already exists. Please sign in."
          : err?.response?.status === 400
            ? "Invalid request. Please check your details."
            : err?.response
              ? "Registration failed. Please try again."
              : "Network error. Please check your connection and try again.");
      setSubmitError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    tradeRole,
    setTradeRole,
    formData,
    handleInputChange,
    isLoading,
    handleSubmit,
    isFormComplete,
    submitError,
    clearSubmitError,
    shopNameStatus,
  };
};
